import { useCallback, useEffect, useRef, useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import {
  KnowledgeLibraryFacets,
  type KnowledgeLibraryFacetsProps,
} from './knowledge-library-facets'
import { useBodyScrollLock } from '../../hooks/use-body-scroll-lock'

const COMPACT_FILTERS_QUERY = '(max-width: 1199px)'
const FOCUSABLE =
  'button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

function useCompactFilters() {
  const [compact, setCompact] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(COMPACT_FILTERS_QUERY)
    const update = () => setCompact(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return compact
}

export function KnowledgeLibraryFilterPanel(props: KnowledgeLibraryFacetsProps) {
  const compact = useCompactFilters()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useBodyScrollLock(compact && open)

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!compact) setOpen(false)
  }, [compact])

  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    const focusable = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
    focusable[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key !== 'Tab' || focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [close, open])

  if (!compact) return <KnowledgeLibraryFacets {...props} />

  return (
    <div className="aw-knowledge-filter-shell">
      <button
        ref={triggerRef}
        type="button"
        className="aw-knowledge-filter-trigger"
        aria-controls="knowledge-filters"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        Filters and views
      </button>
      {open && (
        <div className="aw-knowledge-filter-backdrop" onMouseDown={close}>
          <div
            id="knowledge-filters"
            ref={dialogRef}
            className="aw-knowledge-filter-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-filters-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <strong id="knowledge-filters-title">Filters and views</strong>
              <button type="button" aria-label="Close filters" onClick={close}>
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>
            <KnowledgeLibraryFacets {...props} />
          </div>
        </div>
      )}
    </div>
  )
}
